import {FC} from 'react'
import {Link} from 'react-router-dom'
import {formatInvoiceCount} from '../../../modules/apps/invoice-management/invoices-list/core/_formatters'
import {getInvoiceEntrySourceInfo} from '../../../modules/apps/invoice-management/invoices-list/core/invoiceEntrySourceHelpers'
import {readDashboardError} from './core/_requests'
import {
  formatDayLabel,
  getErroredListUrl,
  getInvoiceUrl,
} from './core/dashboardActivityPresentation'
import {useDashboardActivity} from './core/useDashboardData'

type Props = {
  companyId: number
}

const DashboardRecentErrors: FC<Props> = ({companyId}) => {
  const {data, isLoading, isError, error, refetch} = useDashboardActivity(companyId)
  const summary = data?.errors
  const groups = summary?.groups || []
  const total = Number(summary?.total || 0)
  const listUrl = getErroredListUrl(data?.windowFromDay)

  return (
    <section className='mp-panel mp-errors' aria-labelledby='mp-errors-title' aria-busy={isLoading}>
      <div className='mp-panel__head'>
        <div>
          <span>نیازمند بررسی</span>
          <h2 id='mp-errors-title'>خطاهای اخیر</h2>
        </div>
        {total > 0 && (
          <Link className='mp-errors__total' to={listUrl} title='مشاهده همه صورتحساب‌های دارای خطا'>
            <strong>{formatInvoiceCount(total)}</strong>
            <span>صورتحساب دارای خطا</span>
          </Link>
        )}
      </div>

      {isLoading ? (
        <div className='mp-errors__loading' aria-hidden='true'>
          <span />
          <span />
        </div>
      ) : isError && !data ? (
        <div className='mp-dashboard-alert' role='alert'>
          <i className='las la-exclamation-circle' aria-hidden='true' />
          <span>{readDashboardError(error, 'خطاهای اخیر دریافت نشد.')}</span>
          <button type='button' className='btn btn-sm btn-light-primary' onClick={() => refetch()}>
            تلاش مجدد
          </button>
        </div>
      ) : groups.length === 0 ? (
        <div className='mp-errors__clear'>
          <i className='las la-check-circle' aria-hidden='true' />
          <span>
            در {formatInvoiceCount(data?.windowDays || 30)} روز گذشته صورتحساب دارای خطا ندارید.
          </span>
        </div>
      ) : (
        <>
          <ul className='mp-errors__list'>
            {groups.map((group) => (
              <li key={group.key} className='mp-errors__item'>
                <div className='mp-errors__top'>
                  {group.code ? (
                    <span className='mp-errors__code' dir='ltr' title='کد خطای سامانه مودیان'>
                      {group.code}
                    </span>
                  ) : (
                    <span
                      className='mp-errors__code is-local'
                      title='پیش از ارسال به سامانه مودیان متوقف شد'
                    >
                      پیش از ارسال
                    </span>
                  )}
                  <span className='mp-errors__count'>
                    {formatInvoiceCount(group.invoices)} صورتحساب
                  </span>
                </div>
                <p className='mp-errors__message'>{group.message || 'متن خطا ثبت نشده است.'}</p>
                <div className='mp-errors__meta'>
                  <span>
                    آخرین: {formatDayLabel(group.lastDay, data?.today, data?.yesterday)} ساعت{' '}
                    {group.lastTime}
                  </span>
                  {group.entryTypes.slice(0, 2).map((entryType) => (
                    <span key={entryType}>{getInvoiceEntrySourceInfo(entryType).shortLabel}</span>
                  ))}
                </div>
                <div className='mp-errors__examples'>
                  {group.examples.map((example) => (
                    <Link key={example.invoiceId} to={getInvoiceUrl(example.invoiceId)}>
                      صورتحساب {example.no || example.invoiceId}
                    </Link>
                  ))}
                </div>
              </li>
            ))}
          </ul>
          <div className='mp-errors__footer'>
            {Number(summary?.sampled || 0) < total && (
              <span>
                گروه‌بندی بر اساس {formatInvoiceCount(summary?.sampled || 0)} خطای آخر است.
              </span>
            )}
            <Link to={listUrl}>
              همه صورتحساب‌های دارای خطا
              <i className='las la-angle-left' aria-hidden='true' />
            </Link>
          </div>
        </>
      )}
    </section>
  )
}

export {DashboardRecentErrors}
